import type { ComponentPropsWithoutRef, FC } from 'react'

import { Tooltip } from '@skytech/ui'
import { NameAvatar } from '@skytech/ui/NameAvatar'
import { Td } from '@skytech/ui/Table/Td'
import BanIcon from 'mdi-react/BanIcon'
import CheckDecagramIcon from 'mdi-react/CheckDecagramIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { useTranslation } from '@/app/translations/translations-hooks'
import Highlight from '@/components/Highlight'
import { LocalDateTime } from '@/components/LocalDateTime'
import { TrLink } from '@/components/TrLink'
import { useIsAdmin, useIsItPurchaser, useIsThreeStepIT } from '@/hooks/access'
import { useLocalDateTime } from '@/hooks/use-local-date-time'
import { APP_ROUTES } from '@/util/app-routes'

import type { CostObject } from '../resources'

type ConditionalTrWrapperProps = {
  id: number
} & Omit<ComponentPropsWithoutRef<'tr'>, 'id'>

const ConditionalTrWrapper: FC<ConditionalTrWrapperProps> = ({
  children,
  id,
  className,
  ...props
}) => {
  const isAdmin = useIsAdmin()
  const isITPurchaser = useIsItPurchaser()

  if (isAdmin || isITPurchaser) {
    return (
      <TrLink path={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(id))} className={className}>
        {children}
      </TrLink>
    )
  }

  return (
    <tr {...props} className={className}>
      {children}
    </tr>
  )
}

const CostObjectListItem: FC<
  CostObject & {
    searchFilter: string
  }
> = ({
  name,
  departmentName,
  departmentCode,
  accountingCode,
  id,
  searchFilter,
  customerName,
  code,
  employeeNo,
  benefitType,
  isHidden,
  email,
  consentGivenDatetime,
  consentRevokedDatetime,
  workLocation,
  onOffBoarding,
  onOffBoardingStatus,
}) => {
  const translate = useTranslation()
  const { formatTime } = useLocalDateTime()
  const { whiteLabel } = useAppContext()
  const isThreeStepIT = useIsThreeStepIT()

  return (
    <ConditionalTrWrapper id={id} className={isHidden ? 'inactive' : ' '}>
      <Td className="hidden md:table-cell">
        <NameAvatar
          id={id}
          name={name}
          status={
            onOffBoarding && onOffBoardingStatus
              ? { onOffBoarding, onOffBoardingStatus }
              : undefined
          }
          whiteLabel={whiteLabel}
          translate={translate}
        />
      </Td>
      <Td>
        <div className="cost-object-name">
          <Highlight pattern={searchFilter} text={name ?? ''} />
        </div>
        {consentGivenDatetime && (
          <div className="mt-1 flex items-center text-xs">
            <CheckDecagramIcon
              className={twJoin(
                'ml-0 mr-0.5 w-3.5',
                isThreeStepIT
                  ? 'fill-threeStepIT-success text-threeStepIT-success'
                  : 'fill-green-500 text-gray-500'
              )}
              id={`consent-indicator-${id}`}
            />
            <Tooltip
              anchorId={`consent-indicator-${id}`}
              content={translate('Consent given at {date}', {
                date: formatTime({ date: consentGivenDatetime }),
              })}
              size="large"
              place="right"
            />

            <LocalDateTime date={consentGivenDatetime} />
          </div>
        )}
        {consentRevokedDatetime && (
          <div className="mt-1 flex items-center text-xs">
            <BanIcon
              className={twJoin(
                'ml-0 mr-0.5 w-3.5',
                isThreeStepIT
                  ? 'fill-threeStepIT-warning text-threeStepIT-codex'
                  : 'fill-orange-500 text-gray-500'
              )}
              id={`consent-indicator-${id}`}
            />
            <Tooltip
              anchorId={`consent-indicator-${id}`}
              content={translate('Consent revoked at {date}', {
                date: formatTime({ date: consentRevokedDatetime }),
              })}
              size="large"
              place="right"
            />

            <LocalDateTime date={consentRevokedDatetime} />
          </div>
        )}
      </Td>

      <Td className="cost-object-code">
        <div>
          <Highlight pattern={searchFilter} text={code} />
        </div>
        <div className="subtitle">
          <Highlight pattern={searchFilter} text={email || ''} />
        </div>
        <div className="subtitle">
          <Highlight pattern={searchFilter} text={workLocation || ''} />
        </div>
      </Td>

      <Td className="hidden md:table-cell">
        <div>
          <Highlight pattern={searchFilter} text={employeeNo ? String(employeeNo) : '-'} />
        </div>
        <div className="subtitle">
          <Highlight pattern={searchFilter} text={accountingCode ? String(accountingCode) : '-'} />
        </div>
      </Td>
      <Td className="hidden md:table-cell">
        <Highlight
          pattern={searchFilter}
          text={translate(`cost_object_benefit_type/${benefitType}`)}
        />
      </Td>

      <Td className="hidden md:table-cell">
        <div>
          <Highlight pattern={searchFilter} text={departmentCode} />
          {' - '}
          <Highlight pattern={searchFilter} text={departmentName} />
        </div>
        <div className="subtitle hidden md:table-cell">
          <Highlight pattern={searchFilter} text={String(customerName)} />
        </div>
      </Td>
    </ConditionalTrWrapper>
  )
}

export default CostObjectListItem
